<template>
  <div class="prevent-select department-graph-page">
    <b-row>
      <b-col :md="isRightLibraryPanelHidden ? '12' : '8'">
        <span class="float-right text-primary hand" v-if="isRightLibraryPanelHidden" @click="toggleRightLibraryPanel">
          <i class="bx bx-show"></i>
          {{ $t("organization-hierarchy.show-library") }}
        </span>

        <h2>{{ $t("organization-hierarchy.text") }}</h2>
        <p class="text-muted">{{ $t("department.tree-hint") }}</p>

        <div class="d-flex justify-content-center mb-4">
          <div class="w-25 mr-4">
            <filter-select id="parentFilter" ref="parentFilter" :selected="parentFilter" @filter="onDepartmentSearch" @changed="onParentSelect" :placeholder="$t('organization-hierarchy.filter-parent')" />
          </div>
          <div class="w-25">
            <filter-select
              id="departmentFilter"
              ref="departmentFilter"
              :selected="departmentFilter"
              @filter="onDepartmentSearch"
              @changed="onDepartmentSelect"
              :placeholder="$t('department.select')"
            />
          </div>
        </div>

        <content-loading v-if="loading" />
        <empty v-else-if="nodes.length <= 1" width="200px">
          <h6 class="text-center">{{ $t("department.no-department") }}</h6>
        </empty>
        <flowy v-else :nodes="nodes" :beforeMove="beforeMove" :beforeAdd="beforeAdd" @add="add" @move="move" @remove="remove"></flowy>
      </b-col>

      <b-col md="4" v-show="!isRightLibraryPanelHidden" class="border-left">
        <b-row>
          <b-col md="12">
            <span class="float-right text-primary hand" @click="toggleRightLibraryPanel">
              <i class="bx bx-hide"></i>
              {{ $t("common.hide") }}
            </span>
          </b-col>
        </b-row>
        <simplebar style="z-index: 1000; max-height: 800px; min-width: 400px;">
          <div>
            <b-tabs class="text-left badge font-size-11 hand" v-model="index">
              <b-tab class="badge-lg text-secondary" :title="$t('department.text')" id="sidebar-department">
                <div class="mt-4">
                  <h3><i class="mdi mdi-storefront-outline"></i> {{ $t("department.plural") }}</h3>

                  <div class="app-search d-none d-lg-block pt-0">
                    <div class="position-relative">
                      <input id="search-department" v-model="libraryRequestSearch" type="text" class="form-control bg-white" :placeholder="$t('common.search')" />
                      <span class="bx bx-search-alt"></span>
                    </div>
                  </div>

                  <flowy-new-block
                    v-for="(block, i) in departmentBlocks"
                    :key="block.preview.title + i"
                    class="q-mr-md"
                    @drag-start="onDragStartNewBlock"
                    @drag-stop="onDragStopNewBlock"
                  >
                    <template v-slot:preview>
                      <graph-block :title="block.preview.title" :description="block.preview.description">
                        <i v-if="block.preview.scope && block.preview.scope.value == 'workspace'" v-b-tooltip.hover :title="$t('workspace.yours')" class="text-muted mdi mdi-hexagon-multiple-outline"></i>
                        <i v-else class="text-muted mdi mdi-database" v-b-tooltip.hover :title="$t('library.belonging-to-the-system')"></i>
                      </graph-block>
                    </template>
                    <template v-slot:node="{}">
                      <graph-node :title="block.node.title" :description="block.node.description" :custom-attribute="true" :entity="block.node.entity" type="Department" />
                    </template>
                  </flowy-new-block>

                  <div v-if="libraryPagination" class="row">
                    <div class="col">
                      <ul class="pagination pagination-rounded mb-0">
                        <b-pagination v-model="libraryRequestPage" :total-rows="libraryPagination.totalDocs" :per-page="libraryPagination.limit" limit="10" size="sm"></b-pagination>
                      </ul>
                    </div>
                  </div>
                </div>
              </b-tab>

              <b-tab class="badge-lg text-secondary" :title="$t('department.new')" id="sidebar-new-department" :icon="'mdi mdi-plus'">
                <div class="mt-3 mb-5">
                  <Form :hide-library="true" :presetParent="presetParent" @onDepartmentSave="onDepartmentSaved" />
                </div>
              </b-tab>

              <recommendation-items :draggable="false" level="department" libraryScopedState="department" organizationScopedState="organizationDepartment" />

              <b-tab v-if="recommendationBlocks.length > 0" class="badge-lg text-secondary" :title="$t('common.recommendations')" id="sidebar-recommendations">
                <div class="mt-4">
                  <h3><i class="mdi mdi-storefront-outline"></i> {{ $t("department.recommendations") }}</h3>

                  <flowy-new-block v-for="(block, i) in recommendationBlocks" :key="'rec' + i" class="q-mr-md" @drag-start="onDragStartNewBlock" @drag-stop="onDragStopNewBlock">
                    <template v-slot:preview>
                      <graph-block :title="block.preview.title" :description="block.preview.description">
                        <i class="text-muted mdi mdi-database" v-b-tooltip.hover :title="$t('library.belonging-to-the-system')"></i>
                      </graph-block>
                    </template>
                    <template v-slot:node="{}">
                      <graph-node :title="block.node.title" :description="block.node.description" :custom-attribute="true" :entity="block.node.entity" type="Department" />
                    </template>
                  </flowy-new-block>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </simplebar>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import RecommendationItems from "@cc/recommendation-items";
import Simplebar from "simplebar-vue";
import Form from "./organization-department-form";
import { buildGraphNodes, canMoveUnder, ROOT_PARENT_ID } from "./graph";

/**
 * Organizasyon şeması — flowy kutu grafiği. TKP-27'de girintili listeye dönüşmüştü; kullanıcı isteğiyle
 * eski grafik görünümü geri getirildi, veri kaynağı olarak `unit` grafiği yerine `department.parent` ağacı (K3) kullanılıyor.
 * Korunan davranışlar: TKP-64 süreç eklenebilir rozeti, TKP-80 yetkililer, süreçlere geçiş, alt/kök birim ekleme,
 * çocuklu silmede uyarı, K3 döngü dayanıklılığı. Kütüphaneden sürükle-bırak ile birim ekleme geri geldi.
 */
export default {
  components: { Form, Simplebar, RecommendationItems },
  data() {
    return {
      loading: false,
      docs: [],
      nodes: [],
      isRightLibraryPanelHidden: true,
      index: 0,
      presetParent: undefined,
      parentFilter: undefined,
      departmentFilter: undefined,
      newDraggingBlock: null,
    };
  },
  computed: {
    organization() {
      return this.$store.getters["organization/selected"];
    },
    libraryPagination() {
      return this.$store.getters["department/pagination"];
    },
    libraryRequest() {
      return this.$store.state.department.request;
    },
    departmentBlocks() {
      return this.toBlocks(this.libraryPagination);
    },
    recommendationBlocks() {
      return this.toBlocks(this.$store.getters["department/recommendations"]);
    },
    libraryRequestPage: {
      get() {
        return this.$store.state.department.request.options.page;
      },
      set(value) {
        this.$store.commit("department/request", { ...this.libraryRequest, options: { ...this.libraryRequest.options, page: value } });
        this.fetchLibraryItems();
      },
    },
    libraryRequestSearch: {
      get() {
        return this.$store.state.department.request.query.name.value;
      },
      set(value) {
        this.$store.commit("department/request", { ...this.libraryRequest, query: { name: { value } } });
        this.fetchLibraryItems();
      },
    },
  },
  watch: {
    departmentBlocks: {
      deep: true,
      handler() {
        this.$forceUpdate(); // sürüklenebilir kutuların yeniden çizilmesi için gerekli
      },
    },
  },
  mounted() {
    this.fetchLibraryItems();
    this.load();
    this.$root.$on("edit", this.edit);
    this.$root.$on("expand", this.expand);
    this.$root.$on("add-child", this.addChild);
  },
  beforeDestroy() {
    this.$root.$off("edit", this.edit);
    this.$root.$off("expand", this.expand);
    this.$root.$off("add-child", this.addChild);
  },
  methods: {
    toBlocks(pagination) {
      if (!pagination || !pagination.docs) return [];
      return pagination.docs.map((i) => ({
        preview: { ...i, title: this.$options.filters.translate(i.name) },
        node: { entity: i, title: this.$options.filters.translate(i.name), description: "" },
      }));
    },
    rebuildNodes() {
      this.nodes = buildGraphNodes(this.docs, {
        organization: this.organization,
        label: (d) => this.$options.filters.translate(d && d.name),
        labels: { processAllowed: this.$t("department.process-allowed") },
        parentId: this.parentFilter && this.parentFilter._id,
        departmentId: this.departmentFilter && this.departmentFilter._id,
      });
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$store.dispatch("organizationDepartment/paginateForSearch", {
          options: { page: 1, limit: 1000, sort: { name: "asc" } },
          query: {},
        });
        this.docs = data?.docs ?? [];
        this.rebuildNodes();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.loading = false;
      }
    },
    fetchLibraryItems() {
      this.$store.dispatch("department/paginate", this.libraryRequest);
    },
    toggleRightLibraryPanel() {
      this.isRightLibraryPanelHidden = !this.isRightLibraryPanelHidden;
    },
    async onDepartmentSearch(value, callback) {
      const response = await this.$store.dispatch("organizationDepartment/paginateForSearch", { options: { limit: 1000 }, query: { name: { value } } });
      callback(response.data?.docs ?? []);
    },
    onParentSelect(item) {
      this.parentFilter = item && item._id ? item : undefined;
      this.rebuildNodes();
    },
    onDepartmentSelect(item) {
      this.departmentFilter = item && item._id ? item : undefined;
      this.rebuildNodes();
    },
    openForm() {
      this.isRightLibraryPanelHidden = false;
      this.index = 1;
    },
    edit(node) {
      if (node.root) return;
      this.presetParent = undefined;
      this.$store.commit("organizationDepartment/editedEntity", node.entity);
      this.openForm();
    },
    addChild(node) {
      this.presetParent = node.root ? undefined : { _id: node.entity._id, name: node.entity.name };
      this.$store.commit("organizationDepartment/editedEntity", undefined);
      this.openForm();
    },
    /** Büyüteç: birimin süreç şeması (kaldırılan üst birim süreç ekranının karşılığı), Varlıklar'a yönlendirme değil. */
    expand(node) {
      if (node.root) return;
      this.$store.commit("organizationDepartment/selectedEntity", node.entity);
      this.$router.push(`/organization/department/${node.entity._id}/processes`);
    },
    onDepartmentSaved() {
      this.presetParent = undefined;
      this.$store.commit("organizationDepartment/editedEntity", undefined);
      this.load();
    },
    onDragStartNewBlock(event) {
      this.newDraggingBlock = event.props;
    },
    onDragStopNewBlock() {
      this.newDraggingBlock = null;
    },
    beforeAdd() {
      return !!this.newDraggingBlock;
    },
    beforeMove({ to, from }) {
      if (!to) return false;
      if (from === null) return this.beforeAdd(); // kütüphaneden yeni kutu
      if (from.parentId === ROOT_PARENT_ID) return false; // organizasyon kökü taşınamaz
      return canMoveUnder(this.nodes, from.id, to.id);
    },
    parentIdOfTarget(targetId) {
      return this.organization && String(this.organization._id) === String(targetId) ? null : targetId;
    },
    async add(event) {
      const libraryItem = event.node.data.entity;
      const parentId = this.parentIdOfTarget(event.node.parentId);
      try {
        await this.$store.dispatch("organizationDepartment/addFromLibrary", { libraryItem, parentId });
        this.$toast.success(this.$t("common.successful"));
        this.load();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async move(event) {
      const entity = event.dragged.entity;
      const parentId = this.parentIdOfTarget(event.to.id);
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return this.load();
      try {
        this.$store.commit("organizationDepartment/editedEntity", entity);
        await this.$store.dispatch("organizationDepartment/save", { ...entity, parent: parentId ? { _id: parentId } : null });
        this.$toast.success(this.$t("common.successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.$store.commit("organizationDepartment/editedEntity", undefined);
        this.load();
      }
    },
    async remove(event) {
      const node = event.node;
      if (node.root) return;
      const childCount = this.nodes.filter((n) => String(n.parentId) === String(node.id)).length;
      const ok = await this.confirm(this.$t(childCount ? "department.delete-with-children" : "alert.approve", { count: childCount }));
      if (!ok) return;
      try {
        await this.$store.dispatch("organizationDepartment/delete", node.entity._id);
        this.$toast.success(this.$t("common.delete-successful"));
        this.load();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customMessage: this.$t("common.delete-failed") });
      }
    },
  },
};
</script>

<style lang="scss">
.prevent-select * {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -o-user-select: none;
}

.flowy-block.draggable-mirror {
  opacity: 0.6;
}

.flowy-drag-handle {
  .card {
    border-radius: 12px;
    overflow: hidden;
  }

  .card-body {
    text-align: left;
  }

  h6 {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 15px !important;
    text-align: center;

    i {
      font-size: 22px !important;
    }
  }
}

#search-department {
  color: black;
}
</style>
